// 261005 E1-P2 — cột trái của chặng ⑤: lọc · danh sách tập (chip chính + dấu phụ) · chọn dải liền · nút viết.
// Mọi quyết định hiển thị nằm ở `scriptStageView.ts` / `episodeRowView.ts`; file này chỉ vẽ.
// 3k-c (User 26/10 «cột bước bên trái», SA B2): danh sách tập TRỞ LẠI cột trái (232px). Chip lấy từ DÒNG TẬP của 3k-a (`rowViews` — Đạt · Đang sửa · Chờ · Máy chưa sửa được · Cần xem · Cần kiểm lại · Đã đẩy)
// và bộ lọc «Cần xem» đếm theo CHÍNH chip đó (`listFilterCounts`), để số «n cần xem» không lệch chip. Cột hẹp: chip dài tự XUỐNG DÒNG dưới tên tập, không cắt chữ (CSS `.wb-ep { flex-wrap: wrap }`).
import React from 'react';
import { Empty, Select } from 'antd';
import Button from './WbButton';
import type { OutlineEpisode } from '../../../types/adaptation';
import type { WorkbenchEpisode } from '../../../types/adaptationWorkbench';
import { Pill } from './WbSlot';
import { EpisodeFilter, FilterCtx, episodeBadges, episodeChip, episodeLabel } from './scriptStageView';
import { listFilterCounts, matchesListFilter } from './episodeRowView';
import type { EpisodeRowView } from './episodeRowView';


const FILTER_LABEL: Record<EpisodeFilter, string> = {
  all: 'Tất cả', review: 'Cần xem', new: 'Tập mới', none: 'Chưa viết', draft: 'Đã viết', chapters: 'Đã sang Chapters',
};
const TONE_PILL: Record<'warn' | 'todo' | 'ok', 'warn' | 'todo' | 'ok'> = { warn: 'warn', todo: 'todo', ok: 'ok' };

interface Props {
  rows: OutlineEpisode[];
  episodes: WorkbenchEpisode[];
  /** 3k-c — dòng tập (chip + câu lý do) theo số tập; thiếu tập nào ⇒ lùi về chip cũ của `episodeChip`. */
  rowViews?: ReadonlyMap<number, EpisodeRowView>;
  selectedEp: number | null;
  onOpen: (ep: number) => void;
  /** Tập có thể chọn để viết (tập trong khung xương). */
  order: number[];
  selected: number[];
  onSelect: (ep: number, shift: boolean) => void;
  selectionText: string;
  onClearSelection: () => void;
  filter: EpisodeFilter;
  onFilter: (f: EpisodeFilter) => void;
  locked: boolean;
  /** Cột hẹp (< 260px): ẩn dấu phụ, chip chữ ngắn. */
  compact: boolean;
  /** Dòng "đang viết kịch bản" (job của phiên này hoặc của workbench). */
  jobLine: string | null;
  /** Nút viết + dòng hướng dẫn (do `ScriptStage` dựng theo `rangeWritePlan`). */
  writeArea: React.ReactNode;
  onGoSplit: () => void;
  /** 261005 E4-P4b — có ⇒ hiện bộ lọc "Tập mới" (tập > `lockedThrough` của đợt vừa xong). */
  filterCtx?: FilterCtx;
}

const EpisodeList: React.FC<Props> = ({
  rows, episodes, rowViews, selectedEp, onOpen, order, selected, onSelect, selectionText, onClearSelection, filter, onFilter, locked, compact, jobLine,
  writeArea, onGoSplit, filterCtx,
}) => {
  const wbByEp = new Map(episodes.map((e) => [e.ep, e]));
  const counts = listFilterCounts(episodes, rowViews ?? new Map(), filterCtx);
  const visible = rows.filter((r) => { const w = wbByEp.get(r.ep); return !w || matchesListFilter(w, rowViews?.get(r.ep), filter, filterCtx); });
  const orderSet = new Set(order);
  const selectedSet = new Set(selected);
  const options = (Object.keys(FILTER_LABEL) as EpisodeFilter[])
    .filter((k) => (k === 'new' ? !!filterCtx && counts.new > 0 : k === 'all' || counts[k] > 0 || k === filter))
    .map((k) => ({ value: k, label: k === 'new' && filterCtx ? `${FILTER_LABEL.new} (${(filterCtx.lockedThrough ?? 0) + 1}+) (${counts.new})` : `${FILTER_LABEL[k]} (${counts[k]})` }));

  return (
    <div className="wb-eplist" data-tour="eplist">
      <div className="wb-eplist-head">
        <Select
          size="small" variant="borderless" style={{ flex: '1 1 170px', minWidth: 0 }} popupMatchSelectWidth={false} value={filter} options={options} aria-label="Lọc tập"
          onChange={(v) => onFilter(v as EpisodeFilter)}
        />
        {counts.review > 0 && (
          <Button size="small" type={filter === 'review' ? 'primary' : 'text'} onClick={() => onFilter(filter === 'review' ? 'all' : 'review')}>
            {counts.review} cần xem
          </Button>
        )}
      </div>
      {jobLine && <div className="wb-eplist-job" role="status">{jobLine}</div>}

      {rows.length === 0 ? (
        <Empty description="Chưa chia tập — làm bước ④ trước" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          <Button onClick={onGoSplit}>Sang ④</Button>
        </Empty>
      ) : visible.length === 0 ? (
        <Empty description="Không có tập nào ở bộ lọc này" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div className="wb-eps" role="list">
          {visible.map((r) => {
            const w = wbByEp.get(r.ep);
            const rv = rowViews?.get(r.ep);
            const chip = w ? episodeChip(w) : null;
            const badges = w && !compact ? episodeBadges(w) : [];
            const label = episodeLabel(r, w, 90);   // tên tối đa 2 dòng ở cột 280px (CSS), nên cho dài hơn 60
            const open = r.ep === selectedEp;
            const chipLabel = rv?.chip.label ?? chip?.label;
            return (
              <div key={r.ep} id={`outline-ref-${r.ep}`} role="listitem" className={`wb-ep${open ? ' wb-ep-open' : ''}`}>
                <input
                  type="checkbox" aria-label={`Chọn tập ${r.ep}`} checked={selectedSet.has(r.ep)}
                  disabled={locked || !orderSet.has(r.ep)} title={orderSet.has(r.ep) ? 'Giữ Shift để chọn cả đoạn' : 'Tập ngoài khung xương — không chọn để viết'}
                  onChange={(e) => onSelect(r.ep, !!(e.nativeEvent as MouseEvent).shiftKey)}
                />
                
                <button
                  type="button" className="wb-ep-name" aria-current={open ? 'true' : undefined}
                  title={[label, chipLabel, rv?.reason, ...badges.map((x) => x.label)].filter(Boolean).join(' · ')} onClick={() => onOpen(r.ep)}
                >
                  {compact ? String(r.ep) : label}
                </button>
                {rv ? <span className={`wb-rchip wb-rchip-${rv.chip.tone} wb-ep-chip-col`}>{rv.chip.label}</span> : chip && <Pill tone={TONE_PILL[chip.tone]}>{chip.label}</Pill>}
                {rv?.short && <div className="wb-ep-why" data-testid="ep-why">{rv.short}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div className="wb-eplist-foot">
        {selectionText && (
          <div className="wb-eplist-sel">
            <span style={{ fontSize: 13 }}>{selectionText}</span>
            <Button size="small" type="link" onClick={onClearSelection}>Bỏ chọn</Button>
          </div>
        )}
        {writeArea}
      </div>
    </div>
  );
};

export default EpisodeList;
